/**
 * AI 화면 공용 버튼 — 시나리오 색(accent)으로 칠하는 조밀한 버튼.
 *
 * AI 카드의 행은 FONT.MICRO(10px) 기준이라 MUI Button 의 최소 크기도 크다. 그래서 화면마다
 * Box component="button" 에 sx 를 직접 써 왔고, 같은 역할인데 테두리·배경·굵기가 조금씩 달랐다
 * (accent 틴트 배경·점선 테두리·채운 배경이 섞여 있었다).
 * 그 시각 언어를 여기 한 곳에 모은다 — 라벨 있는 버튼은 이 컴포넌트를 쓴다.
 *
 * variant
 *   solid : 시나리오 색으로 채운다. 그 행에서 사용자가 할 주된 행동(추가 · 상세 화면으로 이동 …).
 *   soft  : 같은 색의 옅은 틴트 + 테두리. 주된 행동 옆에 두는 보조 행동.
 * size
 *   sm : 행 안쪽 / md : 블록 하단
 *
 * @param {"solid"|"soft"} variant
 * @param {"sm"|"md"}      size
 * @param {string}         accent   시나리오 색 — 카드마다 다르므로 호출부가 넘긴다
 * @param {boolean}        disabled
 * @param {Function}       onClick
 * @param {object}         sx       배치용 오버라이드
 * @param {node}           children 라벨 — i18n 은 호출부에서 t() 로 해석해 넘긴다
 * 그 외 prop(aria-expanded·title 등)은 button 요소로 그대로 넘어간다.
 */
import { Box } from "@mui/material";
import { C } from "@plannel/components/ai/aiTokens";
import { FONT } from "@plannel/components/ai/typographyTokens";

const SIZE = {
  sm: { px: "8px", py: "2px", fontSize: FONT.MICRO },
  md: { px: "12px", py: "5px", fontSize: FONT.CAPTION },
};

export function AiButton({ variant = "solid", size = "md", accent, disabled, onClick, sx, children, ...rest }) {
  const s = SIZE[size] || SIZE.md;
  // accent 는 시나리오마다 달라 '밝은 버전' 토큰이 없다 — solid 는 밝기로, soft 는 틴트 농도로 호버를 준다.
  const tone = variant === "soft"
    ? { border: `1px solid ${accent}60`, bgcolor: `${accent}0D`, color: accent,
        "&:hover": { bgcolor: `${accent}1A` } }
    : { border: "none", bgcolor: accent, color: "#fff",
        "&:hover": { filter: "brightness(1.08)" } };
  return (
    <Box component="button" type="button" disabled={disabled} onClick={onClick} {...rest}
      sx={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center",
            borderRadius: "6px", px: s.px, py: s.py, fontFamily: "inherit", lineHeight: 1.4,
            fontSize: s.fontSize, fontWeight: 700, whiteSpace: "nowrap",
            cursor: disabled ? "not-allowed" : "pointer",
            ...tone,
            "&:disabled": { border: "none", bgcolor: C.border, color: C.text3,
                            cursor: "not-allowed", filter: "none" },
            ...sx }}>
      {children}
    </Box>
  );
}

export default AiButton;
